/**
 * Supabase Storage image-transformer helpers.
 * Rewrites a Supabase storage URL to the on-the-fly resize/convert endpoint:
 *   /storage/v1/render/image/public/{bucket}/{path}?width=&height=&format=&quality=&resize=
 *
 * Transforms are computed once per (size, format) and cached on Supabase's CDN.
 * Pass any Supabase storage URL — signed, public, or already-render — and we
 * normalize it. Non-Supabase URLs pass through unchanged.
 */

export type ImgFormat = "origin" | "webp" | "avif" | "jpeg" | "png";

// Matches Supabase storage URLs in any of these shapes:
//   /storage/v1/object/sign/{bucket}/{path}?token=...
//   /storage/v1/object/public/{bucket}/{path}
//   /storage/v1/object/authenticated/{bucket}/{path}
//   /storage/v1/render/image/{public|sign|authenticated}/{bucket}/{path}?...
const STORAGE_RE =
  /^(https?:\/\/[^/]+\/storage\/v1)\/(?:object|render\/image)\/(?:sign|public|authenticated)\/([^/?#]+)\/([^?#]+)/;

export function cdnImageUrl(
  url: string | null | undefined,
  opts: {
    width?: number;
    height?: number;
    quality?: number;
    format?: ImgFormat;
    resize?: "cover" | "contain" | "fill";
  } = {},
): string {
  if (!url) return "";
  const m = url.match(STORAGE_RE);
  if (!m) return url; // not a Supabase storage URL — pass through unchanged
  const [, base, bucket, path] = m;
  const params = new URLSearchParams();
  if (opts.width) params.set("width", String(Math.round(opts.width)));
  if (opts.height) params.set("height", String(Math.round(opts.height)));
  if (opts.quality) params.set("quality", String(opts.quality));
  if (opts.format && opts.format !== "origin") params.set("format", opts.format);
  if (opts.resize) params.set("resize", opts.resize);
  const q = params.toString();
  return `${base}/render/image/public/${bucket}/${path}${q ? `?${q}` : ""}`;
}

/** Build a srcset string for an array of widths. */
export function imageSrcSet(
  url: string | null | undefined,
  widths: number[],
  opts: { format?: ImgFormat; quality?: number; height?: (w: number) => number } = {},
): string {
  if (!url) return "";
  const q = opts.quality ?? 78;
  return widths
    .map((w) => {
      const h = opts.height ? opts.height(w) : undefined;
      return `${cdnImageUrl(url, { width: w, height: h, format: opts.format, quality: q, resize: "cover" })} ${w}w`;
    })
    .join(", ");
}

/** Strip the Supabase signed-URL token from a storage URL (now-public bucket). */
export function stripSignedToken(url: string | null | undefined): string {
  if (!url) return "";
  // Convert /object/sign/{bucket}/{path}?token=... → /object/public/{bucket}/{path}
  return url.replace(
    /\/storage\/v1\/object\/sign\/([^/]+)\/([^?#]+)(?:\?[^#]*)?/,
    "/storage/v1/object/public/$1/$2",
  );
}
